import React, { Component } from "react";
import classNames from "classnames";
import { FormField } from "react-form";

import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";

import { withStyles } from "@material-ui/core";

const styles = theme => ({
    label: {
        marginBottom: 4
    },
    toolbar: {
        border: "1px solid #979797",
        borderBottom: "none",
        borderRadius: "4px 4px 0 0",
        backgroundColor: "#EEEEEE"
    },
    editable: {
        borderRadius: "0 0 4px 4px",
        border: "1px solid #979797",
        padding: "8px 12px",
        minHeight: 225,
        backgroundColor: "#fff",
        overflow: "auto",
        "&:focus": {
            outline: "none",
            boxShadow: "0 0 0 0.2rem rgba(153,0,51,.50)"
        }
    },
    disabled: {
        backgroundColor: "#EEEEEE",
        color: "#000"
    },
    wordCount: {
        backgroundColor: "#EEEEEE",
        padding: 4,
        marginBottom: 4,
        border: "1px solid #979797",
        borderRadius: 4
    },
    red: {
        color: "#be0000 !important"
    }
});

// Plain-text word count, ignoring markup added by the bold/italic/underline commands.
function countWords(html) {
    const div = document.createElement("div");
    div.innerHTML = html || "";
    const text = div.textContent || div.innerText || "";
    return text.trim().split(/\s+/).filter(Boolean).length;
}

// See https://react-form.js.org/#/custom-input
class RichTextFieldWrapper extends Component {
    editorRef = React.createRef();
    lastValidHtml = "";

    componentDidMount() {
        const { fieldApi } = this.props;
        const value = fieldApi.getValue() || "";
        this.lastValidHtml = value;
        if (this.editorRef.current) {
            this.editorRef.current.innerHTML = value;
        }
    }

    placeCaretAtEnd = el => {
        el.focus();
        if (window.getSelection && document.createRange) {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }
    };

    exec = command => {
        const { disabled } = this.props;
        if (disabled) return;
        this.editorRef.current.focus();
        document.execCommand(command, false, null);
        this.handleInput();
    };

    handlePaste = e => {
        e.preventDefault();
        const text = (e.clipboardData || window.clipboardData).getData("text/plain");
        document.execCommand("insertText", false, text);
        this.handleInput();
    };

    handleInput = () => {
        const { fieldApi, maxWords, eventHandle, eventHandleProps1, eventHandleProps2 } = this.props;
        const { setValue, getFieldName } = fieldApi;
        const html = this.editorRef.current.innerHTML;

        if (maxWords && countWords(html) > parseFloat(maxWords)) {
            // Reject the edit that pushed the content over the limit
            this.editorRef.current.innerHTML = this.lastValidHtml;
            this.placeCaretAtEnd(this.editorRef.current);
            return;
        }

        this.lastValidHtml = html;
        setValue(html);
        if (eventHandle) eventHandle(html, getFieldName(), eventHandleProps1, eventHandleProps2);
    };

    render() {
        const { classes, label, disabled, maxWords, fieldApi } = this.props;
        const count = countWords(fieldApi.getValue());
        const textLimit = maxWords && parseFloat(maxWords);
        const overLimit = !!(textLimit && count >= textLimit);

        return (
            <div>
                {label && (
                    <Typography className={classes.label} variant="body2">
                        {label}
                    </Typography>
                )}
                <div className={classes.toolbar}>
                    <Tooltip title="Bold">
                        <span>
                            <IconButton
                                size="small"
                                disabled={disabled}
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => this.exec("bold")}>
                                <FormatBoldIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Italic">
                        <span>
                            <IconButton
                                size="small"
                                disabled={disabled}
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => this.exec("italic")}>
                                <FormatItalicIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Underline">
                        <span>
                            <IconButton
                                size="small"
                                disabled={disabled}
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => this.exec("underline")}>
                                <FormatUnderlinedIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </div>
                <Typography
                    component="div"
                    variant="body2"
                    ref={this.editorRef}
                    className={classNames(classes.editable, disabled && classes.disabled)}
                    contentEditable={!disabled}
                    suppressContentEditableWarning
                    onInput={this.handleInput}
                    onPaste={this.handlePaste}
                />
                {maxWords && (
                    <Typography className={classNames(classes.wordCount, overLimit && classes.red)}>
                        Word Count: {count} out of {maxWords}
                    </Typography>
                )}
            </div>
        );
    }
}

const RichTextField = FormField(RichTextFieldWrapper);

export default withStyles(styles)(RichTextField);
